import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Autocomplete,
  Dialog,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import * as React from "react";

import { useAnalytics } from "@/providers/analytics";
import { useDomainServices } from "@/providers/DomainServices";
import { RobotoIconButton, RobotoTypography } from "@/shared/components";
import { useLayout } from "@/shared/domain/layouts/hooks";
import { RobotoButton } from "@/shared/foundation/primitives";
import { ErrorMonitoringService } from "@/shared/services";
import {
  type EventVisibility,
  type WorkspaceSettingsDefaults,
  SYSTEM_DEFAULT_SETTINGS,
  actions,
  useVizDispatch,
  useVizState,
} from "@/shared/state/visualization";

import { useTimeDisplayMode } from "../../WorkspaceCtx/hooks";
import {
  TIME_DISPLAY_MODES,
  TIME_DISPLAY_MODE_LABELS,
  TimeDisplayMode,
} from "../../WorkspaceCtx/TimeDisplayMode";
import { useWorkspaceEvents } from "../../WorkspaceCtx/useWorkspaceEvents";

import styles from "./WorkspaceSettingsDialog.module.css";

const TITLE_ID = "workspace-settings-dialog-title";

interface WorkspaceSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

/** How far the last "Save as my defaults" click got. */
type DefaultsSaveState = "idle" | "saving" | "error";

/**
 * Edits the workspace-level settings recorded into a shared config: the event
 * visibility rule, the time display mode, and (read-only) the layout
 * reference. Every control applies live — the dialog edits session state, and
 * only Share makes it durable, which the footer says outright.
 */
export function WorkspaceSettingsDialog({
  open,
  onClose,
}: WorkspaceSettingsDialogProps) {
  const state = useVizState();
  const dispatch = useVizDispatch();
  const { configService } = useDomainServices();
  const { trackEvent } = useAnalytics();

  // Settings are dense from seed, so the fallbacks are belt-and-braces for
  // the brief window before a session is seeded (e.g. tests mounting the
  // dialog against INITIAL_STATE).
  const events = state.settings?.events ?? SYSTEM_DEFAULT_SETTINGS.events;
  const overrideCount = Object.keys(state.events ?? {}).length;

  // Chip options: the workspace's own event names, deduped and sorted. The
  // rule stores names verbatim, so a recorded name whose event is absent in
  // this session simply matches nothing until such an event exists again.
  const eventsQuery = useWorkspaceEvents();
  const eventNameOptions = React.useMemo(
    () =>
      [...new Set((eventsQuery.data ?? []).map((event) => event.name))].sort(),
    [eventsQuery.data],
  );
  // Shared with the timeline picker, so the two surfaces cannot disagree.
  const [timeDisplayMode, setTimeDisplayMode] = useTimeDisplayMode();
  const layoutId = state.settings?.layoutId ?? SYSTEM_DEFAULT_SETTINGS.layoutId;

  const [saveState, setSaveState] = React.useState<DefaultsSaveState>("idle");
  const [savedDefaults, setSavedDefaults] =
    React.useState<WorkspaceSettingsDefaults | null>(null);

  // The button reports "saved" only while the saved value still is what the
  // dialog shows. Every edit dispatches a fresh `events` object and settles a
  // new mode, so an edit after a save reverts the label and invites a re-save
  // rather than leaving it claiming that stale defaults are current.
  const showsSaved =
    savedDefaults !== null &&
    isEqual(savedDefaults, { events, timeDisplayMode });

  const applyEvents = (rule: EventVisibility) => {
    dispatch(actions.setWorkspaceSettings({ events: rule }));
  };

  const handleModeChange = (mode: string) => {
    if (mode === "all" || mode === "none") {
      applyEvents({ mode });
      return;
    }
    // Always a fresh empty selection: `RadioGroup` fires only on a change of
    // checkedness, so the mode here is never already `named` — and the
    // `all`/`none` variants carry no list to preserve a draft in.
    applyEvents({ mode: "named", names: [] });
  };

  const handleSaveAsDefaults = () => {
    // `layoutId` and `version` are deliberately absent, and the type forbids
    // adding either: the per-user default layout is `user_default_layout_id`'s
    // job, and the epoch marks a recorded workspace config, not this key.
    const defaults: WorkspaceSettingsDefaults = { events, timeDisplayMode };

    setSaveState("saving");
    configService
      .setUserConfig("workspace_settings_defaults", defaults)
      .then(() => {
        setSavedDefaults(defaults);
        setSaveState("idle");
      })
      .catch((error: unknown) => {
        ErrorMonitoringService.captureError(error);
        setSaveState("error");
      });
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      onClose={onClose}
      aria-labelledby={TITLE_ID}
    >
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <TuneIcon className={styles.titleIcon} />
          <RobotoTypography id={TITLE_ID} variant="h6">
            Workspace settings
          </RobotoTypography>
        </div>
        <RobotoIconButton onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </RobotoIconButton>
      </div>

      <div className={styles.section}>
        <RobotoTypography className={styles.sectionTitle}>
          Events
        </RobotoTypography>
        <RadioGroup
          aria-label="Events"
          value={events.mode}
          onChange={(event) => handleModeChange(event.target.value)}
        >
          <FormControlLabel
            value="all"
            control={<Radio size="small" />}
            label="Show all"
          />
          <FormControlLabel
            value="none"
            control={<Radio size="small" />}
            label="Hide all"
          />
          <FormControlLabel
            value="named"
            control={<Radio size="small" />}
            label="Show selected"
          />
        </RadioGroup>
        {events.mode === "named" && (
          <>
            <Autocomplete
              multiple
              size="small"
              className={styles.ruleInput}
              options={eventNameOptions}
              value={events.names}
              onChange={(_, names) => applyEvents({ mode: "named", names })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    events.names.length === 0 ? "Pick event names" : undefined
                  }
                  inputProps={{
                    ...params.inputProps,
                    "aria-label": "Event names",
                  }}
                />
              )}
            />
            <RobotoTypography
              className={classNames(styles.caption, styles.ruleInput)}
            >
              Pick event names from this workspace. An empty selection shows all
              events.
            </RobotoTypography>
          </>
        )}
        <RobotoTypography className={styles.caption}>
          Events you toggle individually keep their setting.
        </RobotoTypography>
        {overrideCount > 0 && (
          <RobotoButton
            variant="secondary"
            className={styles.resetOverrides}
            onClick={() => dispatch(actions.clearEventOverrides())}
          >
            Reset {overrideCount} individually-toggled{" "}
            {overrideCount === 1 ? "event" : "events"}
          </RobotoButton>
        )}
      </div>

      <div className={styles.section}>
        <RobotoTypography className={styles.sectionTitle}>
          Time display
        </RobotoTypography>
        <Select
          size="small"
          className={styles.modeSelect}
          value={timeDisplayMode}
          SelectDisplayProps={{ "aria-label": "Time display mode" }}
          onChange={(event) => {
            // `SelectChangeEvent` unions in a plain `ChangeEvent`, whose
            // `value` is a bare string, so no generic argument narrows this
            // for us. Every option below is a `TIME_DISPLAY_MODES` member.
            const newMode = event.target.value as TimeDisplayMode;
            trackEvent("TimeDisplayModeChanged", {
              previousMode: timeDisplayMode,
              newMode,
            });
            setTimeDisplayMode(newMode);
          }}
        >
          {TIME_DISPLAY_MODES.map((mode) => (
            <MenuItem key={mode} value={mode}>
              {TIME_DISPLAY_MODE_LABELS[mode]}
            </MenuItem>
          ))}
        </Select>
        <RobotoTypography className={styles.caption}>
          How the timeline clock reads.
        </RobotoTypography>
      </div>

      <div className={styles.section}>
        <RobotoTypography className={styles.sectionTitle}>
          Built from
        </RobotoTypography>
        <LayoutLabel layoutId={layoutId} />
        <RobotoTypography className={styles.caption}>
          The layout this workspace was built from. Panels keep their
          arrangement even if that layout changes. N/A means the layout cannot
          be read.
        </RobotoTypography>
      </div>

      <div className={styles.footer}>
        <RobotoTypography className={styles.disclosure}>
          Applies to your current session. Share to keep these settings in a
          link.
        </RobotoTypography>
        <div className={styles.footerActions}>
          <RobotoButton
            variant="secondary"
            loading={saveState === "saving"}
            onClick={handleSaveAsDefaults}
          >
            {showsSaved ? "Saved as defaults" : "Save as my defaults"}
          </RobotoButton>
          <RobotoButton variant="primary" onClick={onClose}>
            Done
          </RobotoButton>
        </div>
        {saveState === "error" && (
          <RobotoTypography role="alert" className={styles.saveError}>
            Saving defaults failed. Your session settings are unaffected — try
            again.
          </RobotoTypography>
        )}
      </div>
    </Dialog>
  );
}

/**
 * The layout the sharer had active, resolved for display. `null` means the
 * session has no saved-layout reference; a reference the viewer cannot read
 * (deleted, or a cross-org share) displays as N/A — the reference is a label,
 * so a label that cannot be read is simply not shown. An em dash stands in
 * while the name is still on its way, or when `useLayout` stays disabled
 * because no organization is selected yet.
 */
function LayoutLabel({ layoutId }: { layoutId: string | null }) {
  const layoutQuery = useLayout(layoutId ?? undefined);

  if (layoutId === null) {
    return <RobotoTypography>None</RobotoTypography>;
  }
  if (layoutQuery.isError) {
    return <RobotoTypography>N/A</RobotoTypography>;
  }
  return <RobotoTypography>{layoutQuery.data?.name ?? "—"}</RobotoTypography>;
}
